import styled, { createGlobalStyle, css } from "styled-components/macro";
import LinearClock from "components/AheadBehind/LinearClock/LinearClock";
import AheadBehindSummaryCharts from "components/AheadBehind/AheadBehindSummaryCharts/AheadBehindSummaryCharts";
import { useResetTrendChartBinSize } from "./DashboardPages/dashboardPagesUtils";
import { TrendChartBinSizesEnum } from "types/enums";
import StayOnCurrentShift from "components/Dashboard/range-selectors/StayOnCurrentShift";
import { useIsStorybook } from "utils/customHooks/storyHooks";
import { TopDowntimesChartConnected } from "components/TopDowntimesChart/TopDowntimesChartConnected";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import {
  BREAKPOINT_DESKTOP,
  getIsCypressRunning,
  useBreakpoints,
} from "constants/constants";
import { Divider } from "@mui/material";
import { useIsDarkMode, useWasLastPageOperatorPage } from "store/atoms";
import {
  cardBorderLightBackground,
  darkModeBackgroundColor,
  lightTextPlaceholder,
} from "constants/colors";
import { ScaleWithWidth } from "../components/SimpleComponents/ScaleWithWidth";
import useWindowSize from "utils/customHooks/useWindowSize";
import {
  CUSTOM_SCROLLBAR_CSS,
  CUSTOM_SCROLLBAR_CSS_HORIZONTAL,
} from "App/CUSTOM_SCROLLBAR_CSS";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import { useLastDataDumpRow } from "components/DataGetter/LastDataDumpRowGetter";
import RangeSelector from "components/Dashboard/range-selectors/RangeSelector";
import CardWithControls from "components/Dashboard/CardWithControls/CardWithControls";
import RangeSelectorConfig from "components/Dashboard/range-selectors/RangeSelectorConfig";
import { useSettings } from "contexts/SettingsManager/SettingsManager";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
import {
  DEFAULT_METRIC_AB_ROWS,
  DEFAULT_NONMETRIC_AB_ROWS,
  useAheadBehindMetrics,
} from "components/AheadBehind/aheadBehindUtils";
import { clamp } from "lodash";
import { useEffect } from "react";
const TOP_ROW_BASE_WIDTH = 1200;
const BOTTOM_LEFT_BASE_WIDTH = 890;
const BOTTOM_CENTERED_BASE_WIDTH = 1150;
const TOP_ROW_MIN_HEIGHT = 215;

/** Dispays AheadBehind feature to be used by machine operators */
export default function OperatorPage() {
  const isStorybook = useIsStorybook();

  const [lastDataDumpRow] = useLastDataDumpRow();
  const skuCode = isStorybook ? "skuCode" : lastDataDumpRow?.skuCode;
  const skuDisplay = isStorybook ? "skuDisplay" : lastDataDumpRow?.skuName;

  // We use trend chart data dumps for LinearClock, so set the binSize here,
  // where we render the <TrendChartBarsDataDumpsGetter>
  useResetTrendChartBinSize({
    newTrendChartBinSize: TrendChartBinSizesEnum.hours,
  });
  const ckey = useCustomerKey();

  // * in future if we want to display TopDowntimesChart for other customers, we can move showTopDowntimes into a feature flag (useFlagsWithDefaults)
  const showTopDowntimes = ckey === "meritmedical-tijuana";

  const { isDesktopOrLarger } = useBreakpoints();
  const [isDarkMode] = useIsDarkMode();

  const { innerWidth, innerHeight } = useWindowSize();
  const alignABCharts =
    innerWidth / innerHeight > 2300 / 1400 ? "top" : "center";
  const horizontalLayoutDesktopOrLarger = isDesktopOrLarger;
  const { translate } = useFlightTranslation();
  const [
    {
      general: { operatorPageEnableRangeSelector },
    },
  ] = useFlightConfig();
  const { shouldStayOnCurrentShift } = useSettings();
  const { operatorPageOnlyMetrics_Shift } = useAheadBehindMetrics(null);
  const numAbMetrics = operatorPageOnlyMetrics_Shift.length;

  /** scale according to how many MORE metrics than the default we have configured
   *
   * e.g. if metrics are ["OEE"], scale to 1 (less than default # of metrics - don't scale)
   *
   * e.g. if metrics are ["OEE", "Uptime", "Avail", "Performance"], scale to 3/4 ()
   */
  let scaleLinearClockByNumAbMetrics = 1;
  let scaleBottomRowByNumAbMetrics = 0.9;
  if (numAbMetrics >= DEFAULT_METRIC_AB_ROWS) {
    scaleLinearClockByNumAbMetrics =
      (DEFAULT_METRIC_AB_ROWS + DEFAULT_NONMETRIC_AB_ROWS) /
      (numAbMetrics + DEFAULT_NONMETRIC_AB_ROWS);
    // bottom row also contains AheadBehindSummaryCharts, so account for that in bottom scale
    scaleBottomRowByNumAbMetrics =
      (0.9 * numAbMetrics) / DEFAULT_METRIC_AB_ROWS;
  }

  const clockScale = clamp(innerWidth / TOP_ROW_BASE_WIDTH, 1, Infinity);
  const topRowBaseHeight = Math.max(
    TOP_ROW_MIN_HEIGHT,
    TOP_ROW_MIN_HEIGHT * (innerWidth / TOP_ROW_BASE_WIDTH)
  );

  const [, setWasLastPageOperatorPage] = useWasLastPageOperatorPage();
  useEffect(() => {
    setWasLastPageOperatorPage(true);
  }, [setWasLastPageOperatorPage]);

  const deltaNumMetrics = numAbMetrics - DEFAULT_METRIC_AB_ROWS;
  return (
    <OperatorPageStyles
      {...{
        $isDarkMode: isDarkMode,
        $clockScale: clockScale,
      }}
    >
      <OperatorPageGlobalStyles />
      {operatorPageEnableRangeSelector ? (
        <CardWithControls
          title=""
          customCss={css`
            text-align: left;
            padding: 16px;
            overflow: visible;
            @media (min-width: ${BREAKPOINT_DESKTOP}px) {
              padding: 16px 64px;
            }
            &&& {
              margin-top: 10px;
              margin-bottom: 0;
              .cardTopbar {
                background: none;
              }
            }
          `}
          shouldOverlayTitle={true}
          showCardBorder={!isDesktopOrLarger}
          CardConfigControls={<RangeSelectorConfig />}
        >
          <RangeSelector />
        </CardWithControls>
      ) : null}
      {!getIsCypressRunning() &&
      (shouldStayOnCurrentShift || !operatorPageEnableRangeSelector) ? (
        <StayOnCurrentShift />
      ) : null}

      <ScaleWithWidth
        // ideally, a future-proof modular version of this dashboard item would use
        // the component's width itself (useContainerDimensions), not the window width (innerWidth)
        style={{
          minHeight: topRowBaseHeight,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
        baseWidth={TOP_ROW_BASE_WIDTH}
        transformOrigin={"top center"}
        enable={innerWidth > TOP_ROW_BASE_WIDTH}
        scaleMultiplier={scaleLinearClockByNumAbMetrics}
      >
        <div className="skuInfo">
          <div className="currentSkuLabel">{translate("Current_SKU")}</div>
          <span className="skuCode">{skuCode}</span>{" "}
          <span className="skuDisplay">{skuDisplay}</span>
        </div>
        <div className="cardItem clockCardItem overflowScrollHz">
          <LinearClock />
        </div>
      </ScaleWithWidth>
      {showTopDowntimes && horizontalLayoutDesktopOrLarger ? (
        <HorizontalLayoutResponsiveStyles
          className="horizontalLayout operatorPageBottomRow cardItem"
          style={{
            transform: `translateY(
            -${deltaNumMetrics * 1.5}vw
          )`,
          }}
        >
          <div
            className="left"
            style={{
              flexGrow: 1,
              ...(alignABCharts === "top" && {
                alignSelf: "flex-start",
                marginTop: "-1vw",
              }),
            }}
          >
            <ScaleWithWidth
              className="bottomRow horizontalLayout"
              transformOrigin={`left ${alignABCharts}`}
              baseWidth={BOTTOM_LEFT_BASE_WIDTH}
              enable={innerWidth > BOTTOM_LEFT_BASE_WIDTH}
              style={{ pointerEvents: "none" }}
              scaleMultiplier={scaleBottomRowByNumAbMetrics}
            >
              <AheadBehindSummaryCharts />
            </ScaleWithWidth>
          </div>
          <Divider orientation="vertical" variant="middle" flexItem />
          <div className="right" style={{ flexGrow: 0.5 }}>
            <TopDowntimesChartConnected numDowntimes={3} />
          </div>
        </HorizontalLayoutResponsiveStyles>
      ) : (
        <ScaleWithWidth
          baseWidth={BOTTOM_CENTERED_BASE_WIDTH}
          transformOrigin={"top center"}
          className={`bottomRow noTopDowntimes ${horizontalLayoutDesktopOrLarger ? "horizontalLayout" : ""}`}
          scaleMultiplier={scaleBottomRowByNumAbMetrics}
          transformTranslate={`0px, ${numAbMetrics === DEFAULT_METRIC_AB_ROWS ? 0 : 1.5 * deltaNumMetrics}vh`}
        >
          <div className="cardItem operatorPageBottomRow cardPadding">
            <AheadBehindSummaryCharts />
          </div>
        </ScaleWithWidth>
      )}
      {showTopDowntimes && !horizontalLayoutDesktopOrLarger ? (
        <div className="verticalLayout cardItem cardPadding">
          <TopDowntimesChartConnected numDowntimes={3} />
        </div>
      ) : null}
    </OperatorPageStyles>
  );
}
const OperatorPageGlobalStyles = createGlobalStyle`@media(min-width: ${BREAKPOINT_DESKTOP}px){
  #root{
    overflow:hidden
  }
  }`;
const SCALE_AB_CHARTS_Y = 0.8;

const HorizontalLayoutResponsiveStyles = styled.div`
  width: 100vw;
  display: flex;
  justify-content: center;
  align-items: center;
  .MuiDivider-root {
    margin: 12px 18px;
  }

  .left {
    display: flex;
    justify-content: right;
  }
  .right {
    width: min-content;
    margin-bottom: auto;
    margin-top: 0.8vw;
    margin-right: 1.8vw;
  }
  [class*="AheadBehindSummaryChartsStyles"] {
    width: fit-content;
    padding-left: 100px;
    transform: scaleY(${SCALE_AB_CHARTS_Y});
    .bar-label,
    .intervalLabel,
    .outputLossLabel,
    .countLabel,
    .count {
      transform: scaleY(${1 / SCALE_AB_CHARTS_Y});
    }
    .targetCountInfo {
      margin-top: 12px;
    }
  }
  [class*="TopDowntimesChart"] {
    .totalDowntime {
      text-align: left;
    }
    .superscript {
      font-size: 1.1vw;
      line-height: 1.4vw;
    }
    .title {
      margin-bottom: 1.8vw;
      font-size: 2.5vw;
      span {
        line-height: 2.7vw;
      }
    }
    .tagName,
    .tagDuration {
      font-size: 1.6vw;
      line-height: 1.8vw;
    }
    .tagBar {
      height: 1.8vw;
      border-radius: 0.1vw;
      margin-bottom: 1.1vw;
    }
    .MuiDivider-root {
      margin: 1.1vw 0 !important;
    }
  }
  .MuiDivider-vertical {
    margin: 0.5vw 1vw;
  }
`;
const OperatorPageStyles = styled.div<{
  $isDarkMode: boolean;
  $clockScale: number;
}>`
  ${CUSTOM_SCROLLBAR_CSS}
  font-family: "Open Sans";
  font-style: normal;
  font-size: 18px;
  line-height: 26px;
  text-align: center;
  letter-spacing: 0.02em;
  display: flex;
  flex-direction: column;
  & {
    overflow: visible;
  }
  @media (min-width: ${BREAKPOINT_DESKTOP}px) {
    overflow-x: hidden;
  }
  width: 100vw;
  .scaleWithWidth {
    &.bottomRow {
      flex-grow: 1;
      padding-bottom: 32px;
    }
  }
  .clockCardItem {
    height: 100%;
  }
  .skuInfo {
    position: relative;
    .currentSkuLabel {
      position: absolute;
      top: 0;
      bottom: 0;
      margin: auto;
      height: fit-content;
      font-size: 10px;
      right: calc(100% + 1.5em);
      white-space: nowrap;
      border-radius: 5px;
      line-height: 20px;
      font-weight: bold;
      padding: 0 0.75em;
      color: ${lightTextPlaceholder};
      border: 1px solid ${lightTextPlaceholder};
    }
    margin: 16px 0 4px;
    .skuCode {
      font-weight: bold;
      margin-right: 0.5em;
    }
    .skuDisplay {
      font-style: italic;
    }
  }

  &&&& {
    .verticalLayout [class*="TopDowntimesChartStyles"] {
      .totalDowntime {
        text-align: center;
      }
      max-width: 900px;
      margin: auto;
    }
    .horizontalLayout [class*="TopDowntimesChartStyles"] {
      margin: 0;
      padding-right: 32px;
      box-sizing: border-box;
    }
  }
  .operatorPageBottomRow {
    background: ${p => (p.$isDarkMode ? darkModeBackgroundColor : "white")};
    @media (min-width: ${BREAKPOINT_DESKTOP}px) {
      background: none;
    }
    flex-grow: 1;
    align-items: center;
    display: flex;
    justify-content: center;
    &:not(.cardPadding) {
      padding: 0px 36px 48px 36px;
    }
    &.cardPadding {
      padding: 18px;
    }
    .count {
      display: flex;
      justify-content: center;
    }
  }

  .overflowScrollHz {
    ${CUSTOM_SCROLLBAR_CSS_HORIZONTAL}
    overflow: auto;
  }
  [class*="LinearClockStyles"] {
    padding: 4px 8px 12px;
    width: fit-content;
    margin: auto;
  }
  @media (min-width: ${BREAKPOINT_DESKTOP}px) {
    &&&&&&& {
      .clockCardItem {
        border: 1px solid ${cardBorderLightBackground};
        padding: 4px 6px;
        border-radius: 4px;
        max-width: calc(
          ${props => (1 / props.$clockScale) * 100}vw -
            ${props => (1 / props.$clockScale) * 64}px
        );
      }
    }

    [class*="LinearClockStyles"] {
      padding: 0;
      .expanded-text:nth-child(2) {
        margin-top: 6px;
        margin-top: 12px;
      }
      .metric-netOutput,
      .metric-teamSize {
        font-weight: bold;
      }
      .metric-teamSize {
        padding-top: 0;
      }
      .metric-outputLossSum {
        padding-top: 4px;
        padding-bottom: 4px;
      }
      [class*="ClockDataCellStyles"] {
        &:last-child {
          margin-right: 0;
        }
        .metric-totalPotentialOutput,
        .metric-netOutput,
        .metric-outputLossSum,
        .metric-teamSize {
          font-size: 18px;
        }
        .clockTime {
          line-height: 28px;
          padding: 0;
          border-radius: 3px;
        }
      }
      [class*="LinearClockLegendStyles"] {
        margin-top: 0;
        margin-bottom: 0;
        .metric-outputLossSum {
          padding: 0;
          height: 28px;
        }
        .metric-teamSize {
          height: 24px;
          padding: 0;
        }
      }
    }

    /* remove card borders/backgrounds to make more room for components */
    background: ${p => (p.$isDarkMode ? darkModeBackgroundColor : "white")};
    min-height: calc(100vh - 64px);
    .cardItem {
      margin: 0;
      border: none;
    }
    .overflowScrollHz {
      max-width: calc(100vw - 64px);
    }
  }
`;
